import React, { ReactNode } from 'react'
import ImageGallery, { ReactImageGalleryItem } from 'react-image-gallery'
import { LYSContainer } from '@core/components/Primitives'
import { useTranslation } from '@core/i18n/i18n'
import { useProductDetailContext } from '@core/utils/models/product/ProductContext'
import { RenderProp, Slot, template } from '@core/utils/templates'
import {
  useFormattedImages,
  useImageSelection,
  getLargeImageSrc,
} from '@core/components/ProductDetail/ProductImage/ProductImageHelper'
import zoomStyles from './index.module.less'
import styles from '@core/components/ProductDetail/ProductImage/index.module.less'
import 'react-image-gallery/styles/css/image-gallery.css'
import ReactImageMagnify from 'react-image-magnify'
import config from '@core/config/config'
import FullsizeImageGallery from '@core/components/FullsizeImageGallery'

interface ProductImageProps {
  className: string
  imagesToShow?: number
}

interface ProductImageSlots {
  selectedImage: RenderProp<{
    src: string
    className: string
    alt: string
    title: string
  }>
  imageHint: RenderProp<void>
}

const ProductImageWithZoom = template<ProductImageProps, ProductImageSlots>(
  ({ className, imagesToShow = 3, slots }) => {
    const { variant } = useProductDetailContext() || {}
    const imageSelection = useImageSelection(variant)
    const formattedImages = useFormattedImages()

    const customRenderer = (item: ReactImageGalleryItem): ReactNode => {
      return (
        <ReactImageMagnify
          {...{
            enlargedImageContainerStyle: {
              zIndex: 9,
              border: '1px solid #e8e8e8',
              position: 'absolute',
              right: '-387px',
              width: '377px',
              height: '377px',
            },
            enlargedImagePortalId: 'largeImageContainer',
            smallImage: {
              alt: item.originalAlt,
              src: item.original,
              isFluidWidth: true,
            },
            largeImage: {
              alt: item.originalAlt,
              src: getLargeImageSrc(item.original),
              width: 1000,
              height: 1000,
            },
          }}
        />
      )
    }

    const { t } = useTranslation()
    if (!variant || !imageSelection.images) return null
    const showCarouselArrows = imageSelection.images.length > imagesToShow
    return (
      <LYSContainer className={className}>
        <Slot render={slots?.selectedImage}>
          <div
            id="largeImageContainer"
            className={zoomStyles.largeImageContainer}
          />
          <div className={styles.selectedImageContainer}>
            {imageSelection.images.length > 0 ? (
              config.features.fullSizeImageGallery.enabled ? (
                <FullsizeImageGallery
                  images={formattedImages}
                  imagesToShow={imagesToShow}
                  renderItemFunction={customRenderer}
                />
              ) : (
                <ImageGallery
                  items={formattedImages}
                  showPlayButton={false}
                  showThumbnails={imageSelection.images.length > 0}
                  showNav={showCarouselArrows}
                  showFullscreenButton={false}
                  renderItem={customRenderer}
                />
              )
            ) : (
              <>{customRenderer(formattedImages[0])}</>
            )}
          </div>
        </Slot>

        <Slot render={slots?.imageHint}>
          <small>{t('productDetail.imageMayDifferHint')}</small>
        </Slot>
      </LYSContainer>
    )
  }
)
export default ProductImageWithZoom
